import { sand, violet } from "@radix-ui/colors";
import { Flex, Text, ThemeProvider } from "@repo/ui";

function Section({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<Flex column gap="md" style={{ width: "100%", marginTop: 12 }}>
			<Text size="xl" weight="medium" color="accent11">
				{title}
			</Text>
			{children}
		</Flex>
	);
}

function Paragraph({ children }: { children: React.ReactNode }) {
	return (
		<Text size="md" color="sand12" style={{ lineHeight: 1.6 }}>
			{children}
		</Text>
	);
}

function BulletList({ items }: { items: string[] }) {
	return (
		<Flex column style={{ paddingLeft: 22, gap: 8 }}>
			{items.map((item, i) => (
				<Flex
					key={i}
					align="flex-start"
					style={{ position: "relative", paddingLeft: 0 }}
				>
					<Text
						size="sm"
						style={{
							color: sand.sand11,
							position: "absolute",
							left: -22,
							width: 24,
						}}
					>
						•
					</Text>
					<Text color="sand12" style={{ lineHeight: "24px", fontSize: 15 }}>
						{item}
					</Text>
				</Flex>
			))}
		</Flex>
	);
}

function NumberedList({ items }: { items: string[] }) {
	return (
		<Flex column style={{ paddingLeft: 22, gap: 8 }}>
			{items.map((item, i) => (
				<Flex
					key={i}
					align="flex-start"
					style={{ position: "relative", paddingLeft: 0 }}
				>
					<Text
						size="sm"
						style={{
							color: sand.sand11,
							position: "absolute",
							left: -22,
							width: 24,
							transform: `translateY(1px)`,
						}}
					>
						{i + 1}.
					</Text>
					<Text color="sand12" style={{ lineHeight: "24px", fontSize: 15 }}>
						{item}
					</Text>
				</Flex>
			))}
		</Flex>
	);
}

function Subsection({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<Flex column gap="sm" style={{ marginTop: 8 }}>
			<Text weight="medium" color="accent12">
				{title}
			</Text>
			{children}
		</Flex>
	);
}

export function OnePager() {
	return (
		<ThemeProvider forceMode="light">
			<Flex
				column
				align="center"
				background="sand1"
				style={{
					color: sand.sand12,
					minHeight: "100vh",
					padding: 32,
				}}
			>
				<Flex
					column
					gap="xl"
					style={{
						maxWidth: 720,
						width: "100%",
					}}
				>
					{/* Header */}
					<Flex column gap="sm">
						<Text size="xxxl" weight="medium" color="accent12">
							Saffron Health
						</Text>
						<Text size="lg" weight="medium" color="sand11">
							AI agents that handle specialist referrals from start to finish
						</Text>
						<Text font="Newsreader" italic light>
							https://saffron.health ⋅ team@saffron.health
						</Text>
					</Flex>

					{/* The Problem */}
					<Section title="The Problem">
						<Paragraph>
							Specialist referrals are one of the most time-consuming and
							error-prone workflows in primary care. Staff spend hours copying
							data across systems, managing prior authorizations, calling
							specialists, packaging medical records, and coordinating patient
							follow-up. Across more than 100 million referrals each year, this
							adds up to tens of millions of hours of manual work.
						</Paragraph>
						<Paragraph>
							Many organizations try to address this by hiring referral
							coordinators or outsourcing referral management, yet the same
							inefficiencies persist. Staff are overworked, and 72% of providers
							report that they or their staff typically refer patients to the
							same specialist rather than evaluating whether another provider
							has more expertise or sooner availability.
						</Paragraph>
						<Paragraph>
							Combined with outdated specialist directories and complex
							insurance rules, this frequently results in patients being routed
							to the wrong provider and experiencing delays in care. Less than
							half of referrals result in the patient successfully attending a
							specialist appointment, but staff often don't have the time to
							follow up and ensure patients make it. Patients are frequently
							referred to specialists who are out of network or unavailable,
							requiring the referral to be redone and creating extra work for
							staff.
						</Paragraph>
					</Section>

					{/* Referrals Drive Revenue */}
					<Section title="Referrals Drive Revenue in Addition to Labor Costs">
						<Subsection title="Capturing In-Network Revenue">
							<Paragraph>
								Referral patterns drive a large share of a health system's
								downstream revenue. Using agents to automate referrals and
								collect accurate referral data makes it possible to:
							</Paragraph>
							<NumberedList
								items={[
									"Proactively identify when patients are being unnecessarily referred out instead of being treated within the system's existing capabilities.",
									"Detect leakage to out-of-network specialists and redirect referrals to appropriate in-network options.",
									"Recognize high-volume conditions/procedures that justify adding new services, equipment, or specialists, enabling the organization to retain more care and capture more revenue opportunities.",
								]}
							/>
							<Paragraph>
								Systems that understand and act on these referral trends can
								retain more patients, reduce leakage, and expand service lines
								strategically.
							</Paragraph>
						</Subsection>

						<Subsection title="Value-Based Care Models">
							<Paragraph>
								Primary care accounts for only about 5 percent of national
								health expenditures, yet it indirectly influences another 80
								percent of total healthcare spending by directing patients to
								the right specialists and keeping patients out of the hospital.
								Optimizing referrals directly impacts costs and outcomes in
								three key ways:
							</Paragraph>
							<NumberedList
								items={[
									"Avoid unnecessary specialist visits: Some providers refer patients to specialists for conditions they could manage in-house, which increases costs unnecessarily.",
									"Reduce out-of-network and expensive procedures: Without careful analysis, referrals can go to out-of-network specialists or result in more costly or preventable procedures.",
									"Prevent ER visits: When patients reach their specialists quickly, it lowers the likelihood of avoidable emergency room visits, one of the largest expenses for value based care organizations.",
								]}
							/>
							<Paragraph>
								By automating referrals, value-based care organizations can save
								millions in avoidable costs, protect shared savings, and ensure
								care is delivered efficiently and effectively.
							</Paragraph>
						</Subsection>

						<Subsection title="Clinics with Federal Funding">
							<Paragraph>
								Funding and grants depend on high-quality care coordination and
								documented referral completion. When referrals are inaccurate or
								fall through the cracks, clinics risk losing critical federal
								dollars and jeopardizing compliance standards.
							</Paragraph>
						</Subsection>

						<Subsection title="Patient Retention and Satisfaction">
							<Paragraph>
								The referral experience is a major driver of patient loyalty.
								Long delays, unclear communication, and poor care coordination
								post visit are common reasons patients will switch primary care
								providers. A reliable and fast referral process strengthens
								patient trust and reduces churn.
							</Paragraph>
						</Subsection>
					</Section>

					{/* Our Solution */}
					<Section title="Our Solution">
						<Paragraph>
							Saffron Health automates the entire referral workflow with AI
							agents that operate as a fully staffed, highly accurate referral
							coordination team. Humans only step in for unusual edge cases. We
							built this system after manually managing referrals for a network
							of rural clinics in West Texas to understand every step of the
							process.
						</Paragraph>
						<Paragraph>
							Today, our agents integrate directly with the EHR to:
						</Paragraph>
						<BulletList
							items={[
								"Run eligibility and insurance checks",
								"Identify in-network specialists based on availability, patient preferences, and provider quality",
								"Call specialist offices to verify schedule openings and insurance acceptance in real time",
								"Complete and submit all prior authorization forms",
								"Package and send the correct medical records",
								"Follow up with both patient and specialist to ensure the appointment is completed and consult notes are returned",
							]}
						/>
						<Paragraph>
							This eliminates the need for large referral teams or outsourcing,
							while improving speed, accuracy, and patient experience. Unlike
							existing tools, Saffron requires no changes to existing workflows.
							Doctors create referrals in the EHR as usual, and our AI agents
							automatically handle every step—saving time without adding new
							work.
						</Paragraph>
					</Section>

					{/* Proven Results */}
					<Section title="Proven Results">
						<Paragraph>
							Across our live clinic network, Saffron Health has:
						</Paragraph>
						<BulletList
							items={[
								"Reduced referral processing time by 82 percent",
								"Found appointments available 55 percent sooner",
								"Cut referral kickbacks by 44 percent",
								"Eliminated more than 100 hours of manual work",
							]}
						/>
						<Paragraph>
							By automating this critical workflow, clinics meaningfully lower
							labor costs, generate higher shared savings, protect their federal
							funding, and improve patient retention by ensuring a faster and
							more dependable path to specialty care.
						</Paragraph>
					</Section>

					{/* Sources */}
					<Section title="Sources">
						<Flex column gap="sm">
							<Text size="sm" color="sand12">
								Jabbarpour, Yalda, et al.{" "}
								<a
									href="https://www.milbank.org/publications/the-health-of-us-primary-care-2024-scorecard-report-no-one-can-see-you-now/"
									target="_blank"
									rel="noopener noreferrer"
									style={{
										color: violet.violet11,
										textDecoration: "underline",
									}}
								>
									"The Health of US Primary Care: 2024 Scorecard Report — No One
									Can See You Now."
								</a>{" "}
								Milbank Memorial Fund, Feb. 2024.
							</Text>
							<Text size="sm" color="sand12">
								Chang, Jessica, Bianca Silva Gordon, and Catalina Desouza.{" "}
								<a
									href="https://healthcostinstitute.org/hcci-originals-dropdown/all-hcci-reports/4-of-health-spending-goes-to-primary-care"
									target="_blank"
									rel="noopener noreferrer"
									style={{
										color: violet.violet11,
										textDecoration: "underline",
									}}
								>
									"4% of Health Spending Goes to Primary Care."
								</a>{" "}
								Health Care Cost Institute, Nov. 2025.
							</Text>
							<Text size="sm" color="sand12">
								American Medical Association.{" "}
								<a
									href="https://www.ama-assn.org/about/ama-research/trends-health-care-spending"
									target="_blank"
									rel="noopener noreferrer"
									style={{
										color: violet.violet11,
										textDecoration: "underline",
									}}
								>
									"Trends in Health Care Spending."
								</a>
							</Text>
						</Flex>
					</Section>
				</Flex>
				<div className="logo-pattern" />
			</Flex>
		</ThemeProvider>
	);
}
